import { Button, CircularProgress, Typography } from "@mui/material"
import { LinkAuthenticationElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { translate } from "app/language/service"
import { CURRENCIES } from "currency/model"
import { IPayment } from "payment/model"
import { paymentState } from "payment/state"
import React, { useState } from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { getUiConfiguration } from "ui/configuration/tools/common"
import { getUserAgency, getUserEmail } from "user/tools/common"

// https://stripe.com/docs/payments/quickstart

interface IPaymentFormProps {
  isProcessingPayment: boolean
  setIsProcessingPayment: React.Dispatch<React.SetStateAction<boolean>>
  onClose(): any
  onSuccess(payment: IPayment): any
}

export const PaymentForm = ({ isProcessingPayment, setIsProcessingPayment, onClose, onSuccess }: IPaymentFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isFormComplete, setIsFormComplete] = useState(false)
  const [email, setEmail] = useState(getUserEmail())
  const isSubmitDisabled = isProcessingPayment || !isReady || !isFormComplete || !stripe || !elements
  return (
    <FlexBox
      sx={{
        "& > form": { width: "100%" }
      }}>
      <form
        id="payment-form"
        onSubmit={async event => {
          event.preventDefault()
          if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return
          }
          setIsProcessingPayment(true)
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: location.href },
            redirect: "if_required"
          })
          if (error) {
            if (error.type === "card_error" || error.type === "validation_error") {
              setErrorMessage(error.message || "")
            } else {
              setErrorMessage(translate("label.error.unexpected"))
            }
            setIsProcessingPayment(false)
          } else {
            if (!paymentIntent) {
              setErrorMessage(translate("label.error.unexpected"))
            } else {
              onSuccess({
                id: paymentState.nextTempEntityId--,
                stripe_payment_intent_id: paymentIntent.id,
                agency_id: getUserAgency().id,
                amount: 0,
                agency_currency: CURRENCIES.CAD,
                created_at: new Date(paymentIntent.created * 1000).toISOString(),
                initiator_email: email,
                initiator_user_id: 0,
                status: "succedeed",
                tax: -1,
                total: -1,
                stripe_customer_id: "-1",
                stripe_invoice_id: "-1"
              })
            }
          }
        }}>
        {!elements && <LinearLoader text={translate("label.loading.data")} />}
        <LinkAuthenticationElement id="link-authentication-element" onChange={event => setEmail(event.value.email)} />
        <PaymentElement
          id="payment-element"
          onReady={() => setIsReady(true)}
          onChange={event => {
            if (isFormComplete !== event.complete) {
              setIsFormComplete(event.complete)
            }
          }}
          options={{
            layout: "tabs",
            defaultValues: { billingDetails: { email: getUserEmail() } }
          }}
        />
        {errorMessage && (
          <Typography color="error" id="payment-message" sx={{ m: "1rem" }}>
            {errorMessage}
          </Typography>
        )}
        <FlexBox
          gap
          jc="end"
          sx={{
            "mt": "1rem",
            "& > #submit": {
              backgroundColor: theme => theme.palette.primary.main,
              color: theme => theme.palette.specialText,
              border: theme => `1px solid ${theme.palette.primary.main}`,
              display: "flex",
              alignItems: "center",
              transition: "all 200ms ease",
              borderRadius: ".125rem",
              cursor: "pointer",
              height: "2.5rem",
              pl: "2rem",
              pr: "2rem",
              fontFamily: getUiConfiguration().font.googleFamily
            },
            "& > #submit:hover": {
              backgroundColor: theme => theme.palette.specialText,
              color: theme => theme.palette.primary.main
            },
            "& > #submit.disabled:hover": {
              backgroundColor: theme => theme.palette.action.disabledBackground,
              color: theme => theme.palette.action.disabled,
              cursor: "default"
            },
            "& #submit.disabled": {
              cursor: "default",
              backgroundColor: theme => theme.palette.action.disabledBackground,
              color: theme => theme.palette.action.disabled,
              border: theme => `1px solid ${theme.palette.action.disabled}`
            }
          }}>
          <Button disabled={isProcessingPayment} onClick={() => onClose()}>
            {translate("button.cancel")}
          </Button>
          <button
            disabled={isSubmitDisabled}
            id="submit"
            className={isSubmitDisabled ? "disabled" : undefined}
            onClick={isSubmitDisabled ? event => event.stopPropagation() : undefined}>
            {isProcessingPayment && (
              <CircularProgress
                size="small"
                sx={{ width: "1.2rem", height: "1.2rem", mr: ".5rem", color: theme => theme.palette.primary.main }}
              />
            )}
            <span id="button-text">{translate("label.pay.now")}</span>
          </button>
        </FlexBox>
      </form>
    </FlexBox>
  )
}
